import type { DomJson } from '@nx/constants';

const jsonDomAttributeKeys = [
  'href',
  'src',
  'alt',
  'caption',
  'language',
  'role',
  'endpoint',
  'search',
  'tab',
  'copy',
  'element',
] as const;

type JsonDomAttributeKey = (typeof jsonDomAttributeKeys)[number];

const isJsonDomAttributeKey = (key: string): key is JsonDomAttributeKey => {
  const keysWidenedType: readonly string[] = jsonDomAttributeKeys;
  return keysWidenedType.includes(key);
};

const createTextNode = (value: string | null = '') => {
  return {
    name: '#text',
    type: Node.TEXT_NODE,
    value,
    classList: [],
    children: [],
  };
};

const rewriteSlideElementToDiv = () => {
  return {
    name: 'DIV',
    classList: ['sectionbody'],
  };
};

const rewritePreformattedTextElementToCodeBlock = (node: HTMLElement | ChildNode) => {
  let runnable = true;
  if (node instanceof HTMLElement) {
    runnable =
      (node.hasAttribute('mode') && node.getAttribute('mode') === 'cypher') || node.classList.contains('runnable');
  }

  return {
    name: runnable ? 'CODEBLOCK' : 'CODE',
    type: Node.ELEMENT_NODE,
    children: [
      {
        name: 'DIV',
        type: Node.ELEMENT_NODE,
        value: '',
        classList: [],
        children: [],
        attributes: {},
      },
      {
        name: 'CONTENT',
        type: Node.ELEMENT_NODE,
        value: '',
        classList: [],
        children: [createTextNode(node.textContent)],
      },
    ],
    attributes: {
      language: 'cypher',
      copy: 'false',
    },
  };
};

const rewriteAnchorElementToRunnableLink = (node: HTMLElement) => {
  const playTopic = node.getAttribute('play-topic') ?? '';
  return {
    name: 'RUNNABLELINK',
    type: Node.ELEMENT_NODE,
    children: [createTextNode(node.textContent)],
    attributes: {
      href: playTopic,
    },
  };
};

const rewriteNodeToDomJson = (node: HTMLElement | ChildNode): DomJson => {
  const result: DomJson = {
    name: node.nodeName,
    type: node.nodeType,
    attributes: {},
    classList: [],
    children: [],
    value: '',
  };

  const rewriteElementFns: Record<string, () => Partial<DomJson>> = {
    SLIDE: () => rewriteSlideElementToDiv(),
    H1: () => ({ name: 'H2' }),
    PRE: () => rewritePreformattedTextElementToCodeBlock(node),
    A: () => {
      return node instanceof HTMLElement && node.getAttribute('play-topic') !== null
        ? rewriteAnchorElementToRunnableLink(node)
        : result;
    },
    // Create empty text nodes for Elements we don't want to render.
    // Refer to https://html5sec.org/ for information on attack vectors.
    // The asciidoc renderer does not currently render these elements,
    // but should this ever change, we want to ensure these aren't rendered for custom guides.
    SCRIPT: () => createTextNode(),
    STYLE: () => createTextNode(),
    SVG: () => createTextNode(),
    IFRAME: () => createTextNode(),
    VIDEO: () => createTextNode(),
    LINK: () => createTextNode(),
    FORM: () => createTextNode(),
    INPUT: () => createTextNode(),
    OBJECT: () => createTextNode(),
    EMBED: () => createTextNode(),
    APPLET: () => createTextNode(),
    BASE: () => createTextNode(),
    FRAMESET: () => createTextNode(),
  };

  const rewriteElementResult = rewriteElementFns[node.nodeName]?.() ?? {};
  return { ...result, ...rewriteElementResult };
};

const convertHtmlToDomJson = (node: HTMLElement | ChildNode): DomJson => {
  const result = rewriteNodeToDomJson(node);

  if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
    for (const attr of node.attributes) {
      if (result.attributes !== undefined && isJsonDomAttributeKey(attr.name)) {
        result.attributes[attr.name] = attr.value;
      }
    }

    for (const child of node.childNodes) {
      const convertedChildNode = convertHtmlToDomJson(child);
      if (convertedChildNode.type !== Node.TEXT_NODE || convertedChildNode.value?.trim() !== '') {
        result.children.push(convertedChildNode);
      }
    }
  } else if (node.nodeType === Node.TEXT_NODE) {
    result.value = node.textContent ?? null;
  }

  return result;
};

const findBestTitleCandidate = (doc: Document): string => {
  const carouselSectionHeading = doc.body.querySelector('article > carousel > h4')?.textContent ?? undefined;

  const titleElement = doc.querySelector('title')?.textContent ?? undefined;

  const [firstSectionHeading] = doc.querySelectorAll('h1, h2, h3, h4, h5, h6').values();
  const firstHeading = firstSectionHeading?.textContent ?? undefined;

  return carouselSectionHeading ?? titleElement ?? firstHeading ?? 'Guide';
};

const transformHtmlToTitleAndContents = (html: string): { title: string; contents: DomJson[] } => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const title = findBestTitleCandidate(doc);
  const slides = Array.from(doc.body.querySelectorAll('article > carousel > slide'));
  const contents = slides.map((slide) => convertHtmlToDomJson(slide));

  if (slides.length === 0 || contents.length === 0) {
    throw new Error('Unable to parse the guide contents.');
  }

  return { title, contents };
};

export default { transformHtmlToTitleAndContents };
